/**
 * Session management hook
 *
 * Provides the current user session state using TanStack Query
 * with proper caching, loading states, and error handling.
 *
 * ## Auth outage grace mode
 *
 * When the API/server is unreachable (network error, 5xx, etc.) and
 * TanStack Query already has cached session data, the hook keeps
 * `session` populated from cache and sets `isNetworkError: true`.
 * This lets the auth provider distinguish a confirmed 401 (user is
 * genuinely unauthenticated) from a transient outage (keep the user
 * inside the app in degraded mode).
 *
 * - 401 → `fetchSession` returns `null` (successful query, data = null).
 * - Network/server error → query throws, TanStack Query keeps previous
 *   `data` in cache, `query.isError` is true.
 */

import { useQuery } from "@tanstack/react-query";
import { ENDPOINTS } from "@floline/api-client";
import type { Session } from "@floline/api-client";
import { API_URL } from "@/env";

/** Query key for session data */
export const SESSION_QUERY_KEY = ["session"] as const;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function getString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function pickUser(value: unknown): Session["user"] | null {
  if (!isRecord(value)) return null;

  const id = getString(value.id);
  const email = getString(value.email);
  const name = value.name === null ? null : getString(value.name);
  const image = value.image === null ? null : getString(value.image);

  if (!id || !email) return null;

  return {
    id,
    email,
    name,
    image,
  };
}

function normalizeSessionPayload(payload: unknown): Session | null {
  // Better Auth returns a raw object like:
  //   { user: {...}, session: { expiresAt: "..." } }
  // Our API client types expect:
  //   { user: {...}, expiresAt: "..." }
  // Some endpoints in this repo wrap responses in { data: ... }, so handle that too.

  const root = isRecord(payload) && "data" in payload ? (payload as UnknownRecord).data : payload;
  if (!isRecord(root)) return null;

  const user = pickUser(root.user);
  if (!user) return null;

  const expiresAt =
    getString(root.expiresAt) ??
    (isRecord(root.session) ? getString(root.session.expiresAt) : null) ??
    new Date().toISOString();

  return { user, expiresAt };
}

/**
 * Fetch the current user session from the API
 *
 * Returns `null` for a confirmed 401 (no session).
 * Throws for network errors / 5xx so TanStack Query retains cached data.
 */
async function fetchSession(): Promise<Session | null> {
  const base = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
  const url = `${base}${ENDPOINTS.auth.session}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  // 401 means no active session - this is expected when not logged in
  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Session request failed (${response.status})`);
  }

  const json = (await response.json()) as unknown;
  return normalizeSessionPayload(json);
}

/**
 * Hook to get the current user session
 *
 * Returns the session data along with loading and error states.
 * Automatically refetches when the window regains focus.
 *
 * @example
 * ```tsx
 * function UserProfile() {
 *   const { session, isLoading, isError } = useSession();
 *
 *   if (isLoading) return <Spinner />;
 *   if (!session) return <LoginButton />;
 *   return <div>Welcome, {session.user.name}</div>;
 * }
 * ```
 */
export function useSession() {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    // Session should be refetched less aggressively
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Don't retry aggressively; treat this as lightweight health for auth cookies.
    retry: (failureCount: number) => failureCount < 2,
  });

  // A network/server error occurred but we still have cached session data.
  // This is the "degraded" scenario: the user was previously authenticated
  // but we can't confirm the session right now.
  // 401 is handled as `data = null` (no throw), so any thrown error is
  // by definition a network/server/parse issue.
  const isNetworkError = query.isError;

  return {
    /** Current session data, or null if not authenticated */
    session: query.data ?? null,
    /** User from the session, or null if not authenticated */
    user: query.data?.user ?? null,
    /** Whether the session is currently being fetched for the first time */
    isLoading: query.isLoading,
    /** Whether the session fetch failed with an error */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Whether the user is authenticated (session exists, possibly from cache) */
    isAuthenticated: !!query.data,
    /**
     * True when the session fetch failed due to a network or server error
     * (NOT a 401). When combined with `isAuthenticated`, indicates the user
     * is in "degraded" / auth-outage-grace mode.
     */
    isNetworkError,
    /** Refetch the session manually */
    refetch: query.refetch,
  };
}
